import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, PackageX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import { adminService } from '../../../../services/adminService';
import { toast } from 'sonner';
import { money, pct, units } from './metrics';

export const ReturnsByProductView = ({ params }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await adminService.getClientPerformanceReturnsByProduct(params));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load returns by product');
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="py-16 text-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500 mx-auto" /></div>;
  }
  if (!data) return null;

  const totals = data.totals || {};
  const maxReturned = Math.max(1, ...(data.products || []).map((p) => p.qty_returned || 0));

  return (
    <div className="space-y-5" data-testid="returns-by-product-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card><CardContent className="p-3">
          <p className="text-xs uppercase tracking-wide text-gray-500">Units returned</p>
          <p className="text-lg font-bold text-rose-600" data-testid="rbp-returned">{units(totals.qty_returned)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs uppercase tracking-wide text-gray-500">Return rate</p>
          <p className="text-lg font-bold">{pct(totals.return_rate_pct)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs uppercase tracking-wide text-gray-500">Credit given</p>
          <p className="text-lg font-bold text-rose-600">{money(totals.credit_given)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <p className="text-xs uppercase tracking-wide text-gray-500">Waste cost</p>
          <p className="text-lg font-bold text-rose-600">{money(totals.waste_cost)}</p>
        </CardContent></Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <PackageX className="h-4 w-4 text-rose-600" /> What comes back
            <span className="ml-1 text-xs font-normal text-gray-500">
              What not to push, and what to cap
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]" data-testid="returns-product-table">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="p-2 text-left">Product</th>
                  <th className="p-2 text-right">Delivered</th>
                  <th className="p-2 text-right">Sold</th>
                  <th className="p-2 text-right">Returned</th>
                  <th className="p-2 text-left w-40">Share of delivered</th>
                  <th className="p-2 text-right">Credit given</th>
                  <th className="p-2 text-right">Waste cost</th>
                  <th className="p-2 text-right">Net revenue</th>
                </tr>
              </thead>
              <tbody>
                {(data.products || []).length === 0 && (
                  <tr><td colSpan={8} className="p-8 text-center text-gray-500">No delivered history in this period.</td></tr>
                )}
                {(data.products || []).map((p) => (
                  <tr key={p.product_id} className="border-t" data-testid={`returns-row-${p.product_id}`}>
                    <td className="p-2 font-medium text-gray-900">{p.product_name}</td>
                    <td className="p-2 text-right">{units(p.qty_delivered)}</td>
                    <td className="p-2 text-right">{units(p.qty_sold)}</td>
                    <td className="p-2 text-right text-rose-600">{units(p.qty_returned)}</td>
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
                          <div
                            className="h-full bg-rose-400"
                            style={{ width: `${((p.qty_returned || 0) / maxReturned) * 100}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-12 text-right">{pct(p.return_rate_pct)}</span>
                      </div>
                    </td>
                    <td className="p-2 text-right">{money(p.credit_given)}</td>
                    <td className="p-2 text-right">{money(p.waste_cost)}</td>
                    <td className="p-2 text-right">{money(p.net_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReturnsByProductView;
